import { useAdminData } from "@/hooks/useAdminData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Building2, CalendarCheck, TrendingUp, CreditCard, Receipt, Euro, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { useMemo } from "react";
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval, isWithinInterval } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2, 160 60% 45%))",
  "hsl(var(--chart-3, 30 80% 55%))",
  "hsl(var(--chart-4, 280 65% 60%))",
  "hsl(var(--chart-5, 340 75% 55%))",
];

const AdminDashboard = () => {
  const { loading, error, organisations, authUsers, reservations, subscriptions, invoices, paidBookings, customers } = useAdminData();

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = startOfMonth(now);
    const thisMonthEnd = endOfMonth(now);
    const lastMonth = startOfMonth(subMonths(now, 1));
    const lastMonthEnd = endOfMonth(subMonths(now, 1));

    // Unique customers (by email, across all organisations)
    const uniqueEmails = new Set(customers.map((c) => c.email?.toLowerCase()).filter(Boolean));
    const uniqueCustomers = uniqueEmails.size;

    // Bookings this month vs last month
    const bookingsThisMonth = reservations.filter((r) => {
      const d = new Date(r.created_at);
      return isWithinInterval(d, { start: thisMonth, end: thisMonthEnd });
    }).length;
    const bookingsLastMonth = reservations.filter((r) => {
      const d = new Date(r.created_at);
      return isWithinInterval(d, { start: lastMonth, end: lastMonthEnd });
    }).length;

    // Active subscriptions & MRR
    const activeSubscriptions = subscriptions.filter((s) => s.status === "active");
    const mrr = activeSubscriptions.reduce((sum, s) => sum + (s.amount || 0), 0);

    // Subscription revenue from paid invoices
    const totalSubscriptionRevenue = invoices
      .filter((i) => i.status === "paid")
      .reduce((sum, i) => sum + (i.amount_incl_tax || 0), 0);

    // Invoices this month
    const invoicesThisMonth = invoices.filter((i) => {
      const d = new Date(i.created_at);
      return isWithinInterval(d, { start: thisMonth, end: thisMonthEnd });
    });
    const paidThisMonth = invoicesThisMonth.filter((i) => i.status === "paid");
    const revenueThisMonth = paidThisMonth.reduce((sum, i) => sum + (i.amount_incl_tax || 0), 0);

    // Paid bookings (reservations that received payment via the pay-at-order module)
    const totalPaidBookings = paidBookings.length;

    return {
      totalOrgs: organisations.length,
      totalUsers: authUsers.length,
      totalBookings: reservations.length,
      uniqueCustomers,
      totalCustomers: customers.length,
      totalPaidBookings,
      bookingsThisMonth,
      bookingsLastMonth,
      activeSubscriptions: activeSubscriptions.length,
      totalSubscriptions: subscriptions.length,
      mrr,
      totalSubscriptionRevenue,
      revenueThisMonth,
      totalInvoices: invoices.length,
      paidInvoices: invoices.filter((i) => i.status === "paid").length,
    };
  }, [organisations, authUsers, reservations, subscriptions, invoices]);

  // User growth chart (last 6 months)
  const userGrowthData = useMemo(() => {
    const now = new Date();
    const months = eachMonthOfInterval({ start: subMonths(startOfMonth(now), 5), end: startOfMonth(now) });
    return months.map((month) => {
      const end = endOfMonth(month);
      const count = authUsers.filter((u) => new Date(u.created_at) <= end).length;
      const newInMonth = authUsers.filter((u) => {
        const d = new Date(u.created_at);
        return isWithinInterval(d, { start: month, end });
      }).length;
      return { month: format(month, "MMM yyyy"), total: count, new: newInMonth };
    });
  }, [authUsers]);

  // Subscription status pie chart
  const subStatusData = useMemo(() => {
    const counts: Record<string, number> = {};
    organisations.forEach((o) => {
      const s = o.subscription_status || "unknown";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [organisations]);

  // Bookings & revenue per month (last 6 months)
  const bookingsData = useMemo(() => {
    const now = new Date();
    const months = eachMonthOfInterval({ start: subMonths(startOfMonth(now), 5), end: startOfMonth(now) });
    return months.map((month) => {
      const end = endOfMonth(month);
      const monthReservations = reservations.filter((r) => {
        const d = new Date(r.created_at);
        return isWithinInterval(d, { start: month, end });
      });
      const count = monthReservations.length;
      const revenue = monthReservations
        .filter((r) => r.status === "confirmed" || r.status === "completed")
        .reduce((s, r) => s + (r.price || 0), 0);
      return { month: format(month, "MMM yyyy"), bookings: count, revenue: Math.round(revenue * 100) / 100 };
    });
  }, [reservations]);

  // Subscription revenue per month (from invoices)
  const subscriptionRevenueData = useMemo(() => {
    const now = new Date();
    const months = eachMonthOfInterval({ start: subMonths(startOfMonth(now), 5), end: startOfMonth(now) });
    return months.map((month) => {
      const end = endOfMonth(month);
      const monthInvoices = invoices.filter((i) => {
        const d = new Date(i.created_at);
        return isWithinInterval(d, { start: month, end }) && i.status === "paid";
      });
      const revenue = monthInvoices.reduce((s, i) => s + (i.amount_incl_tax || 0), 0);
      return { month: format(month, "MMM yyyy"), revenue: Math.round(revenue * 100) / 100, invoices: monthInvoices.length };
    });
  }, [invoices]);

  const bookingChange = stats.bookingsLastMonth > 0
    ? Math.round(((stats.bookingsThisMonth - stats.bookingsLastMonth) / stats.bookingsLastMonth) * 100)
    : stats.bookingsThisMonth > 0 ? 100 : 0;

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Organisations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold">{stats.totalOrgs}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold">{stats.totalUsers}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Bookings</CardTitle>
            <CalendarCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalBookings}</p>
            <div className="flex items-center gap-1 mt-1">
              {bookingChange >= 0 ? (
                <ArrowUpRight className="h-3 w-3 text-emerald-500" />
              ) : (
                <ArrowDownRight className="h-3 w-3 text-destructive" />
              )}
              <span className={`text-xs ${bookingChange >= 0 ? "text-emerald-500" : "text-destructive"}`}>
                {bookingChange}% vs last month
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Paid Bookings</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold">{stats.totalPaidBookings}</p></CardContent>
        </Card>
      </div>

      {/* KPI Cards - Row 2: Revenue & Subscriptions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Subscriptions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.activeSubscriptions}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.totalSubscriptions} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">MRR (Monthly Recurring)</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold">€{stats.mrr.toFixed(2)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Subscription Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">€{stats.totalSubscriptionRevenue.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.paidInvoices} paid invoices</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unique Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.uniqueCustomers}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.totalCustomers} total (incl. duplicates)</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth */}
        <Card>
          <CardHeader><CardTitle className="text-base">User Growth (6 months)</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Legend />
                <Line type="monotone" dataKey="total" stroke={CHART_COLORS[0]} name="Total Users" strokeWidth={2} />
                <Line type="monotone" dataKey="new" stroke={CHART_COLORS[1]} name="New Users" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardHeader><CardTitle className="text-base">Subscription Status</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={subStatusData} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                  {subStatusData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Bookings Volume */}
        <Card>
          <CardHeader><CardTitle className="text-base">Bookings per Month</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookingsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Bar dataKey="bookings" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} name="Bookings" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Subscription Revenue per Month */}
        <Card>
          <CardHeader><CardTitle className="text-base">Subscription Revenue per Month (€)</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={subscriptionRevenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} formatter={(value: number) => `€${value.toFixed(2)}`} />
                <Legend />
                <Bar dataKey="revenue" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} name="Revenue" />
                <Bar dataKey="invoices" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} name="Invoices" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
